import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { defineStore } from 'pinia';
import { useLoading } from '@sa/hooks';
import { SetupStoreId } from '@/enum';
import { useRouterPush } from '@/hooks/common/router';
import {
  fetchGetEmailToken,
  fetchGetVerifyToken,
  fetchLogin,
  fetchLoginOut,
  fetchPostSystemInfo,
  fetchPostUserEmailCode,
  fetchResetPassword
} from '@/service/api';
import { localStg } from '@/utils/storage';
import { $t } from '@/locales';
import { useAppStore } from '@/store/modules/app';
import { useRouteStore } from '../route';
import { useDataCenterStore } from '../data-center';
import { setAuthorizationToken } from '../data-center/shared';
import { clearAuthStorage, getStaticSuper, getToken, getUserInfo, getUserMenuId } from './shared';

export const useAuthStore = defineStore(
  SetupStoreId.Auth,
  () => {
    const route = useRoute();
    const routeStore = useRouteStore();
    const { toLogin, redirectFromLogin } = useRouterPush(false);
    const { loading: loginLoading, startLoading, endLoading } = useLoading();
    const { getDataCenterList } = useDataCenterStore();

    /** is super role in static route */
    const isStaticSuper = ref(getStaticSuper());

    /** Is login */
    const token = ref(getToken());
    const isLogin = computed(() => Boolean(token.value));

    /** 当前用户的menuId */
    const menuIdList = reactive({ value: getUserMenuId() || [] });

    const menus = computed(() => routeStore.menus);
    const appStore = useAppStore();

    /** 当前logo systemName */
    const systemInfo: Api.Auth.systemInfo = reactive({
      logo: '',
      systemName: '',
      nickname: '',
      avatar: '',
      email: '',
      tenantId: ''
    });

    const firmwareTrans: any = reactive({})

    /** Reset auth store 强制清缓存退出到登录页 */
    async function resetStore() {
      const authStore = useAuthStore();

      clearAuthStorage();

      authStore.$reset();

      await toLogin();
      routeStore.resetStore();
    }
    /** 退出登录 */
    async function loginOut() {
      startLoading();
      const { data, error } = await fetchLoginOut();
      endLoading();
      if (!error) {
        resetStore();
      }
    }

    /**
     * Login
     *
     * @param account User name
     * @param password Password
     * @param appUuid Password
     * @param channel Password
     * @param [redirect=true] Whether to redirect after login. Default is `true`
     */
    async function login(account: string, password: string, appUuid: string, channel: string, redirect = true) {
      startLoading();

      const emptyBack: Api.Auth.LoginBack = {
        dataCenterId: 1,
        isActivate: 1,
        menuIds: [],
        roleId: '',
        roleName: '',
        token: '',
        systemName: '',
        tenantUuid: '',
        userSession: {
          userModel: {
            account: '',
            avatar: '',
            email: '',
            gender: '',
            nickname: ''
          },
          userId: '',
          tenantId: '',
          sessionId: ''
        }
      };
      const { data: loginBack, error } = await fetchLogin(account, password, appUuid, channel);
      if (!error) {
        // 登录成功接口返回内容缓存
        loginBackFun(loginBack || emptyBack);
        isStaticSuper.value = getStaticSuper();
        menuIdList.value = getUserMenuId() || [];

        await routeStore.initAuthRoute();

        // 跨数据中心
        getDataCenterList(loginBack.dataCenterId);
        setAuthorizationToken(loginBack.token);

        // 多语言
        appStore.updateLocalString();
        appStore.reloadPage();


        if (redirect) {
          // 重定向
          await redirectFromLogin();
        }
      } else {
        resetStore();
      }

      endLoading();
    }
    function loginBackFun(loginBack: Api.Auth.LoginBack) {
      const { dataCenterId, isActivate, menuIds, roleId, roleName, systemName, systemLogo, tenantUuid } = loginBack;
      const { tenantId, userId } = loginBack.userSession;
      const { account, avatar, email, gender, nickname } = loginBack.userSession.userModel;
      const personInfo = {
        dataCenterId,
        isActivate,
        menuIds,
        roleId,
        roleName,
        tenantId,
        userId,
        account,
        avatar,
        email,
        gender,
        nickname,
        systemName,
        tenantUuid
      };

      localStg.set('personInfo', personInfo);
      localStg.set('token', loginBack.userSession.sessionId);

      Object.assign(systemInfo, { nickname, email, avatar, tenantId, logo: systemLogo, systemName });
    }

    /**
     * 获取验证码
     *
     * @param email
     */
    let emailToken: string = '';
    async function getEmailToken(email: string) {
      startLoading();
      // 重置状态
      verifyRes = false;

      const { data: token, error } = await fetchGetEmailToken({ email });
      endLoading();

      if (!error && token) {
        emailToken = token || '';
        window.$message?.success?.($t('page.login.resetPwd.sendCodeSuccess'));
        return emailToken;
      }
    }

    let verifyRes: boolean = false;
    async function getVerifyToken(code: string, receiver: string) {
      const { data: verify, error } = await fetchGetVerifyToken({ code, token: emailToken, receiver });

      if (!error) {
        if (verify === 0) {
          verifyRes = true;
        } else if (verify === 1) {
          verifyRes = false;
          window.$message?.error?.($t('page.login.resetPwd.checkCodeFail'));
        } else if (verify === -1) {
          verifyRes = false;
          window.$message?.error?.($t('page.login.resetPwd.checkCodeNotFound'));
        }
      }
      return verify === 0;
    }

    async function getResetVerifyToken(code: string) {
      const { data: verify, error } = await fetchPostUserEmailCode({ code, token: emailToken });

      if (!error) {
        if (verify) {
          verifyRes = true;
          window.$message?.success?.($t('page.login.resetPwd.checkCodeSuccess'));
        } else {
          verifyRes = false;
          window.$message?.error?.($t('page.login.resetPwd.checkCodeFail'));
        }
      }
      return verify;
    }

    /** 忘记密码 */
    async function resetPassword(email: string, password: string, code: string) {
      if (!verifyRes) {
        window.$message?.error?.($t('page.login.resetPwd.checkCodeFail'));
        return;
      }
      startLoading();
      const { data: reset, error } = await fetchResetPassword({
        code,
        token: emailToken,
        email,
        newPassword: password
      });
      console.log(reset);

      endLoading();
      if (!error) {
        emailToken = '';
        return true;
      }
      return false;
    }

    /** 获取系统名称，logo等信息 */
    async function getSystemInfo() {
      startLoading();
      const { data, error } = await fetchPostSystemInfo({ domainName: window.location.hostname });

      // const { data, error } = await fetchPostSystemInfo({ domainName: 'iot-platform.gvssmart.top' });

      endLoading();
      if (!error && data) {
        Object.assign(systemInfo, data);
      }
    }

    function setSystemInfo(data: Api.Auth.systemInfo) {
      Object.assign(systemInfo, data);
    }

    return {
      token,
      isStaticSuper,
      isLogin,
      loginLoading,
      resetStore,
      loginOut,
      login,
      getEmailToken,
      menuIdList,
      getUserInfo,
      getVerifyToken,
      resetPassword,
      getResetVerifyToken,
      getSystemInfo,
      systemInfo,
      firmwareTrans,
      setSystemInfo
    };
  },
  {
    persist: [
      { key: 'IOT_SYSTEM_TITLE', paths: ['systemInfo'] },
      { key: 'IOT_FIRMWARE_TYPE', paths: ['firmwareTrans'] },
    ]
  }
);
